import React from 'react';
import {Box, styled} from '@mui/material';
import {designSystemToken, Headline} from '@lightricks/react-design-system';
import {CollaborationGigCard} from '@/components/gig-cards';
import useInvitedGigsQuery from '@/hooks/queries/gigs/use-invited-gigs-query';
import translate from '@/utils/translate';
import {InvitedGig} from '@/types/gigs';
import useIsMobile from '@/hooks/use-is-mobile';
import LEGACY_COLORS from '@/styles/legacyColors';
import {scrollbarMixins} from '@/styles/mixins';
import RecruitedIcon from '@/components/icons/Recruited';
import {media} from '@/styles/mediaQueries';
import Link from '@/components/link';
import useGenericButtonPressedEvent from '@/hooks/analytics/use-generic-button-pressed-event';
import {screenNames} from '@/lib/delta/deltaConstants';

const SHOW_SEE_ALL_INVITES_THRESHOLD = 3;

const StyledHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  marginBottom: '25px',
  [media.mobile]: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const StyledHeadline = styled(Headline)(() => ({
  [media.mobile]: {
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '1.43',
    letterSpacing: '0.15px',
  },
}));

const StyledAllInvitesLink = styled(Box)(() => ({
  cursor: 'pointer',
  [media.mobile]: {
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '1.43',
    letterSpacing: '0.15px',
  },
}));

const StyledGrid = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  columnGap: '20px',
  [media.mobile]: {
    'flexWrap': 'nowrap',
    'overflowX': 'auto',
    'gap': '10px',
    'paddingBottom': '10px',
    'marginLeft': '-20px',
    'marginRight': '-20px',
    'paddingLeft': '20px',
    'paddingRight': '20px',
    ...scrollbarMixins.overrideScrollbarHidden(),
    '& > *': {
      flex: '0 0 auto',
      width: 'calc(100vw - 40px)', // Full viewport width minus left/right padding
      minWidth: 'calc(100vw - 40px)',
    },
  },
}));

const isRecruited = (gig: InvitedGig) => gig.recruitedAt !== undefined;

function MyInvitesSectionView() {
  const {
    gigs: invitedGigs,
    count,
    isLoading,
    isError,
    error,
  } = useInvitedGigsQuery({previewFetch: true});
  const {pressed} = useGenericButtonPressedEvent();

  const trackButtonPressed = (buttonName: string) => {
    pressed({
      button_name: buttonName,
      screen_name: screenNames.suggested_gigs.my_invites,
    });
  };
  const isMobile = useIsMobile();

  if (isLoading) {
    return null;
  }

  // Silently fail on error for now, or show a subtle message
  if (isError) {
    console.error('Error loading invited gigs:', error);
    return null; // Don't render the section if invites fail to load
  }

  if (invitedGigs.length === 0) {
    return null; // Don't render the section if there are no invites
  }

  const shouldShowSeeAllInvites =
    count > SHOW_SEE_ALL_INVITES_THRESHOLD || (isMobile && count > 1);

  return (
    <>
      <StyledHeader>
        <StyledHeadline size="xl">{translate('My Invites')}</StyledHeadline>
        {shouldShowSeeAllInvites && (
          <StyledAllInvitesLink>
            <Link
              to="/gigs"
              style={{color: LEGACY_COLORS.BLUE}}
              onClick={() =>
                trackButtonPressed(
                  translate('All invites ({count}) >', {count})
                )
              }
            >
              {translate('All invites ({count}) >', {count})}
            </Link>
          </StyledAllInvitesLink>
        )}
      </StyledHeader>
      <StyledGrid>
        {invitedGigs.map((gig) => (
          <Link
            key={gig.id}
            to={`/campaigns/${gig.id}`}
            onClick={() => trackButtonPressed(`My Invite: ${gig.campaignName}`)}
          >
            <CollaborationGigCard
              gigData={gig}
              tag={
                isRecruited(gig)
                  ? {
                      text: translate('Gig for you'),
                      icon: <RecruitedIcon width={17} height={18} />,
                      color: LEGACY_COLORS.BLUE,
                    }
                  : undefined
              }
            />
          </Link>
        ))}
      </StyledGrid>
    </>
  );
}

export default MyInvitesSectionView;
